/*
 * Copyright 2026 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
package kpt.core.data.di

import kpt.core.data.alerts.AlertsRepository
import kpt.core.data.alerts.impl.AlertsRepositoryImpl
import kpt.core.data.alerts.providePriceAlertOutbox
import kpt.core.data.alerts.providePriceAlertSubmitSyncer
import kpt.core.data.banking.BillReminderRepository
import kpt.core.data.banking.LoanRepository
import kpt.core.data.banking.impl.BillReminderRepositoryImpl
import kpt.core.data.banking.impl.LoanRepositoryImpl
import kpt.core.data.banking.provideBillReminderOutbox
import kpt.core.data.banking.provideBillReminderSubmitSyncer
import kpt.core.data.banking.provideLoanCalcScenarioOutbox
import kpt.core.data.banking.provideLoanOutbox
import kpt.core.data.banking.provideLoanSubmitSyncer
import kpt.core.data.calc.AmortizationCalcRepository
import kpt.core.data.calc.impl.AmortizationCalcRepositoryImpl
import kpt.core.data.cloudtodo.CloudTodoRepository
import kpt.core.data.cloudtodo.impl.CloudTodoRepositoryImpl
import kpt.core.data.cloudtodo.provideCloudTodoBookkeeper
import kpt.core.data.cloudtodo.provideCloudTodoSyncOrchestrator
import kpt.core.data.crypto.CryptoRepository
import kpt.core.data.crypto.impl.CryptoRepositoryImpl
import kpt.core.data.currency.CurrencyRepository
import kpt.core.data.currency.impl.CurrencyRepositoryImpl
import kpt.core.data.economic.EconomicRatesRepository
import kpt.core.data.economic.MacroIndicatorsRepository
import kpt.core.data.economic.impl.EconomicRatesRepositoryImpl
import kpt.core.data.economic.impl.MacroIndicatorsRepositoryImpl
import kpt.core.data.emi.EmiCalculatorRepository
import kpt.core.data.emi.impl.EmiCalculatorRepositoryImpl
import kpt.core.data.profile.ProfileRepository
import kpt.core.data.profile.impl.ProfileRepositoryImpl
import kpt.core.data.user.UserDataRepository
import kpt.core.data.user.impl.UserDataRepositoryImpl
import kpt.core.data.user.provideAuthHeaderBridge
import kpt.core.data.user.provideAuthTokenSource
import kpt.core.data.watchlist.WatchlistRepository
import kpt.core.data.watchlist.impl.WatchlistRepositoryImpl
import kpt.core.store.config.AppStoreRegistry
import org.koin.core.module.Module
import org.koin.core.qualifier.named
import org.koin.dsl.module

/**
 * GENERATED from `@RepositoryBinding` — one Koin binding per repository. DO NOT HAND-EDIT.
 *
 * To add a repository: annotate the implementation `@RepositoryBinding(binds = X::class)` and
 * mark its store parameters `@FromStore("<storeId>")`. There is no wiring step — the
 * constructor IS the dependency list.
 *
 * Bindings that carry behaviour (submit syncers, the cloud-todo orchestrator, outbox
 * serializers) stay hand-written in `DemoRepositoryModule`: a lambda body is not a
 * dependency list, and eagerness is a lifecycle decision a constructor cannot state.
 *
 * Pulled in by `DataModule` via `includes(GeneratedRepositoryBindings)`.
 */
val GeneratedRepositoryBindings: Module = module {
    single<kpt.core.base.network.AuthHeaderBridge>(createdAtStart = true) {
        provideAuthHeaderBridge(
            registry = get(),
            tokenSource = get(),
            headers = get(),
            preferences = get(),
            scope = get(),
        )
    }
    single<kpt.core.base.network.AuthTokenSource> {
        provideAuthTokenSource(
            preferences = get(),
        )
    }
    single<kpt.core.base.store.submit.SubmitOutbox<kpt.core.model.banking.BillReminder>>(qualifier = named("outbox.billReminder")) {
        provideBillReminderOutbox(
            dao = get(),
        )
    }
    single<kpt.core.data.banking.BillReminderSubmitSyncer>(createdAtStart = true) {
        provideBillReminderSubmitSyncer(
            scope = get(),
            outbox = get(named("outbox.billReminder")),
            networkMonitor = get(),
            repository = get(),
        )
    }
    single<org.mobilenativefoundation.store.store5.Bookkeeper<kpt.core.store.cloudtodo.impl.CloudTodoKey>> {
        provideCloudTodoBookkeeper(
            dao = get(),
        )
    }
    single<kpt.core.store.cloudtodo.impl.CloudTodoSyncOrchestrator>(createdAtStart = true) {
        provideCloudTodoSyncOrchestrator(
            scope = get(),
            networkMonitor = get(),
            bookkeeperDao = get(),
            bookkeeper = get(),
            todoDao = get(),
            repository = get(),
            crashReporter = getOrNull(),
        )
    }
    single<kpt.core.base.store.submit.SubmitOutbox<kpt.core.model.banking.LoanCalcScenario>>(qualifier = named("outbox.loanCalcScenario")) {
        provideLoanCalcScenarioOutbox(
            dao = get(),
        )
    }
    single<kpt.core.base.store.submit.SubmitOutbox<kpt.core.model.banking.Loan>>(qualifier = named("outbox.loan")) {
        provideLoanOutbox(
            dao = get(),
        )
    }
    single<kpt.core.data.banking.LoanSubmitSyncer>(createdAtStart = true) {
        provideLoanSubmitSyncer(
            scope = get(),
            outbox = get(named("outbox.loan")),
            networkMonitor = get(),
            repository = get(),
        )
    }
    single<kpt.core.base.store.submit.SubmitOutbox<kpt.core.model.alerts.PriceAlert>>(qualifier = named("outbox.priceAlert")) {
        providePriceAlertOutbox(
            dao = get(),
        )
    }
    single<kpt.core.base.store.submit.OfflineSubmitSyncer<kpt.core.model.alerts.PriceAlert, kpt.core.model.alerts.PriceAlert>>(createdAtStart = true) {
        providePriceAlertSubmitSyncer(
            scope = get(),
            outbox = get(named("outbox.priceAlert")),
            networkMonitor = get(),
            repository = get(),
        )
    }
    single<AlertsRepository> {
        AlertsRepositoryImpl(
            alertsStore = get(AppStoreRegistry.Alerts),
            alertsWriteStore = get(AppStoreRegistry.AlertsMutable),
        )
    }
    single<AmortizationCalcRepository> {
        AmortizationCalcRepositoryImpl(
            store = get(AppStoreRegistry.AmortizationCalc),
        )
    }
    single<BillReminderRepository> {
        BillReminderRepositoryImpl(
            billRemindersStore = get(AppStoreRegistry.BillReminders),
            billRemindersWriteStore = get(AppStoreRegistry.BillRemindersMutable),
            billReminderDao = get(),
        )
    }
    single<CloudTodoRepository> {
        CloudTodoRepositoryImpl(
            readStore = get(AppStoreRegistry.CloudTodo),
            writeStore = get(AppStoreRegistry.CloudTodoMutable),
            gateway = get(),
        )
    }
    single<CryptoRepository> {
        CryptoRepositoryImpl(
            coinMarketsStore = get(AppStoreRegistry.CoinMarkets),
            coinDetailStore = get(AppStoreRegistry.CoinDetail),
        )
    }
    single<CurrencyRepository> {
        CurrencyRepositoryImpl(
            exchangeRatesStore = get(AppStoreRegistry.ExchangeRates),
            rateHistoryStore = get(AppStoreRegistry.RateHistory),
            spotRateStore = get(AppStoreRegistry.SpotRate),
        )
    }
    single<EconomicRatesRepository> {
        EconomicRatesRepositoryImpl(
            interestRateSeriesStore = get(AppStoreRegistry.InterestRateSeries),
        )
    }
    single<EmiCalculatorRepository> {
        EmiCalculatorRepositoryImpl(
            emiStore = get(AppStoreRegistry.Emi),
        )
    }
    single<LoanRepository> {
        LoanRepositoryImpl(
            loansStore = get(AppStoreRegistry.Loans),
            loansWriteStore = get(AppStoreRegistry.LoansMutable),
            loanDao = get(),
        )
    }
    single<MacroIndicatorsRepository> {
        MacroIndicatorsRepositoryImpl(
            macroIndicatorStore = get(AppStoreRegistry.MacroIndicator),
        )
    }
    single<ProfileRepository> {
        ProfileRepositoryImpl(
            profileStore = get(AppStoreRegistry.Profile),
        )
    }
    single<UserDataRepository> {
        UserDataRepositoryImpl(
            preferencesRepository = get(),
            userDataStore = get(AppStoreRegistry.UserData),
        )
    }
    single<WatchlistRepository> {
        WatchlistRepositoryImpl(
            watchlistStore = get(AppStoreRegistry.Watchlist),
            watchlistWriteStore = get(AppStoreRegistry.WatchlistMutable),
            dao = get(),
        )
    }
}
